import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/core/actions/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/core/avatar.tsx";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket.tsx";
import { Button } from "@/components/core/buttons/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import Tooltip from "@/components/core/tooltip";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { publicProfileQueryOptions } from "@/features/dashboard/api/queries/public-profile-query";
import { CaseItem } from "@/features/dashboard/components/case-item";
import { IconAwardFilled, IconDatabaseExclamation, IconDots } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

export const Route = createFileRoute("/_dashboard/profile_/$profileId")({
	validateSearch: z.object({ status: z.string().optional() }),
	loaderDeps: ({ search }) => ({ status: search.status }),
	loader: ({ context: { queryClient }, params, deps }) => {
		return queryClient.ensureQueryData(publicProfileQueryOptions(params.profileId, deps.status || "all"));
	},
	component: ProfileComponent,
});

function ProfileComponent() {
	const params = Route.useParams();

	const queryParams = new URLSearchParams(window.location.search);
	const [activeTab, setActiveTab] = useState(queryParams.get("status") || "all");
	const navigate = useNavigate();
	const profileQuery = useSuspenseQuery(publicProfileQueryOptions(params.profileId, activeTab));
	const data = profileQuery.data;

	const setUrl = async (status: string) => {
		setActiveTab(status);
		await navigate({
			to: `/profile/${params.profileId}`,
			search: (old) => ({ ...old, status: status }),
			replace: true,
		});
	};

	return (
		<Content pageName="Cases">
			{/*<Header title="Cases" />*/}
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="relative flex flex-row items-center w-full p-8 rounded-md border border-border/50">
					<div className="flex flex-row items-center w-full gap-4">
						<div className="inline-flex justify-center items-center w-[80px] h-[80px] rounded-full bg-primary-600 text-primary-foreground">
							{/* <IconScale size={32} stroke={2} /> */}
							<Avatar className="w-[80px] h-[80px] rounded-full bg-primary-600">
								{!data.avatar ? (
									<AvatarFallback className="text-lg font-bold">{data.initials}</AvatarFallback>
								) : (
									<AvatarImage src="/avatar/44198079c5211172a61405b5049f3bfb.jpg" />
								)}
							</Avatar>
						</div>
						<div className="inline-flex flex-col w-auto">
							<div className="inline-flex flex-row items-center w-auto gap-1">
								<span className="font-bold text-xl text-foreground-dark">{data.full_name}</span>
								<Tooltip content="Bar Top-notcher 1950">
									<div className="inline-flex text-yellow-600">
										<IconAwardFilled size={20} stroke={2} />
									</div>
								</Tooltip>
							</div>
							<span className="text-base text-foreground/70">{data.email}</span>
							{data.roll_number && <span className="text-base text-foreground/70">Roll No. {data.roll_number}</span>}
						</div>
					</div>
					<div className="absolute top-4 right-4 inline-flex self-start shrink-0 flex-row w-auto ml-auto gap-2">
						<DropdownMenu modal={false}>
							<DropdownMenuTrigger asChild>
								<Button className="!rounded-full" type="button" size="icon" variant="ghost">
									<IconDots size={14} stroke={2} />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<DropdownMenuGroup>
									<DropdownMenuItem onSelect={() => alert(1)}>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
											<span className="inline-flex">Some Action</span>
										</div>
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => alert(1)}>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
											<span className="inline-flex">Another Action</span>
										</div>
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={() => alert(1)}>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
											<span className="inline-flex">More Actions</span>
										</div>
									</DropdownMenuItem>
									<DropdownMenuSeparator />
									<DropdownMenuItem onSelect={() => alert(1)} dangerZone>
										<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
											<span className="inline-flex">Danger Zone</span>
										</div>
									</DropdownMenuItem>
								</DropdownMenuGroup>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>

				<div className="flex flex-col w-full gap-4">
					<Tabs defaultValue="all" onValueChange={(value) => setUrl(value)}>
						<TabsList>
							<TabsTrigger value="all">All Cases Involved</TabsTrigger>
							<TabsTrigger value="active">Active Cases Involved</TabsTrigger>
							<TabsTrigger value="closed">Closed Cases Involved</TabsTrigger>
						</TabsList>
						<TabsContent value={activeTab}>
							{/* <ProfileCaseList /> */}
							{data.case_records && data.case_records.length ? (
								data.case_records.map((record, index) => <CaseItem data={record} key={index} />)
							) : (
								<div className="flex flex-col w-full rounded-md border border-border/50 mt-6">
									<Blanket width="sm">
										<BlanketImage icon={<IconDatabaseExclamation size={24} stroke={1.5} />} />
										<BlanketInfo title="No Data" message="No available data for now. Please check back later or refresh the page." />
									</Blanket>
								</div>
							)}
						</TabsContent>
					</Tabs>
				</div>
			</div>
		</Content>
	);
}
